import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'query-string';
import OktaWidgetWrapper, {
  isLoginRedirect,
  getWithRedirect,
  hideWidget,
} from '../../components/OktaWidget/okta-widget-wrapper';
import { getAppsFromAccessToken, setOrRemoveRedirectUrl } from '../../okta';
import 'url-polyfill';
import { ROUTES } from '../../navigation/router';
import { useOktaAuth } from '@okta/okta-react';
import Loader from '@tcl-boron-prefabs/loader';
import logger from '../../utils/logger';
import cn from 'classnames';
import withStyles from '@tempus/component-library/utils/withStyles';
import styles from '../../styles';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';

function SignIn({ classes }) {
  const history = useHistory();
  const { authState } = useOktaAuth();
  const [loading, setLoading] = useState(false);
  const [ehrLogout, setEhrLogout] = React.useState(false);

  const navToAppSwitcher = useCallback(async () => {
    if (authState && authState.isAuthenticated) {
      const apps = getAppsFromAccessToken(authState.accessToken);
      history.push(ROUTES.APP_SWITCHER, { apps });
    }
  }, [authState, history]);

  const handleFromLoginRedirect = useCallback(async () => {
    // If we are redirected back from okta after SSO login
    // Get an access token, read it, and redirect
    hideWidget();

    await getWithRedirect();
  }, []);

  const tryRedirecting = useCallback(
    async (queryObj) => {
      const hasFromLoginQueryParam = queryObj?.fromLogin || isLoginRedirect();
      const hasAutoRedirectQueryParam = queryObj?.autoRedirect;
      const hasRedirectQueryParam = queryObj?.redirect;
      const knownButDeferredQueryParams = { fromEHR: queryObj?.fromEHR };

      // Set or remove ?redirect= if available
      // Actual redirect handled by AppSwitcher
      setOrRemoveRedirectUrl(queryObj?.redirect);

      // Handle ?fromLogin= if available
      if (hasFromLoginQueryParam) {
        await handleFromLoginRedirect();
      }
      // Handle ?redirect= if available
      else if (hasRedirectQueryParam) {
        await navToAppSwitcher();
      }
      // Handle ?autoRedirect=true if available
      // May not be used anymore
      else if (hasAutoRedirectQueryParam) {
        await getWithRedirect();
      } else if (Object.values(knownButDeferredQueryParams).some((param) => param)) {
        // Known query params that are deferred due to requiring the widget to be mounted
        console.debug('Deferring known query param', { queryObj });
      } else {
        logger.error('Unknown redirect state', queryObj);
      }
    },
    [handleFromLoginRedirect, navToAppSwitcher],
  );

  useEffect(() => {
    const queryObj = qs.parse(window.location.search);
    // Redirect if query params are present
    if (Object.keys(queryObj).length > 0) {
      tryRedirecting(queryObj);
    } else {
      if (!authState || !authState.isAuthenticated) {
        setOrRemoveRedirectUrl();
      }
    }

    // Only when authenticated, no-op otherwise
    navToAppSwitcher();
    setLoading(false);
  }, [authState, tryRedirecting, history, navToAppSwitcher]);

  useEffect(() => {
    async function isEhrLogout() {
      const urlSearchParams = new URLSearchParams(window.location.search);
      if (!!urlSearchParams.get('ehrLogout')) {
        setEhrLogout(true);
        urlSearchParams.delete('ehrLogout');
        history.replace({
          search: urlSearchParams.toString(),
        });
      }
    }
    isEhrLogout();
  }, []); // eslint-disable-line

  if (ehrLogout) {
    return (
      <div className={cn('app-view-container')}>
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            Logged out due to inactivity
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Relaunch the application to log back in.
          </p>
        </>
      </div>
    );
  }

  if (loading || authState === undefined)
    return (
      <div className={cn('app-view-container', classes.loadingContainer)}>
        <Loader />
      </div>
    );

  return <OktaWidgetWrapper />;
}

export default withStyles(styles)(SignIn);
